import type { NextRequest } from 'next/server';
import type { IncomingMessage } from 'node:http';

import { isNonProductionEnv } from './isProductionEnv';

export const CLIENT_IP_HEADER = 'x-client-ip';

const CLIENT_IP_HEADERS = [
  'cf-connecting-ip',
  'x-real-ip',
  'x-forwarded-for',
] as const;

function normalizeIp(value: string | undefined): string | undefined {
  if (!value) return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function readFirstForwardedIp(value: string): string | undefined {
  const first = value.split(',')[0]?.trim();
  return normalizeIp(first);
}

function readIpFromHeaders(
  getHeader: (name: string) => string | undefined,
): string | undefined {
  for (const headerName of CLIENT_IP_HEADERS) {
    const value = getHeader(headerName);
    if (!value) continue;

    const ip =
      headerName === 'x-forwarded-for'
        ? readFirstForwardedIp(value)
        : normalizeIp(value);
    if (ip) return ip;
  }

  return undefined;
}

function readClientIpOverride(search: string): string | undefined {
  if (!isNonProductionEnv() || !search) return undefined;

  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search,
  );
  return normalizeIp(params.get('client_ip') ?? undefined);
}

export function resolveClientIpFromReq(
  req: IncomingMessage | undefined,
  urlSearch = '',
): string | undefined {
  const override = readClientIpOverride(urlSearch);
  if (override) return override;

  const fromHeaders = readIpFromHeaders((name) => {
    const header = req?.headers?.[name];
    return Array.isArray(header) ? header[0] : header;
  });
  if (fromHeaders) return fromHeaders;

  if (isNonProductionEnv()) {
    return normalizeIp(process.env.DEV_CLIENT_IP);
  }

  return undefined;
}

export function resolveClientIp(request: NextRequest): string | undefined {
  const override = readClientIpOverride(request.nextUrl.search);
  if (override) return override;

  const fromHeaders = readIpFromHeaders(
    (name) => request.headers.get(name) ?? undefined,
  );
  if (fromHeaders) return fromHeaders;

  if (isNonProductionEnv()) {
    return normalizeIp(process.env.DEV_CLIENT_IP);
  }

  return undefined;
}
